import {useState, useEffect, useRef} from 'react';
import {useSelector} from 'react-redux';
import {Box, Divider, Grid, Typography} from '@mui/material';
import Header from './Header';
import {SourcesState} from './schema';
import './index.scss';
import SelectionHeader from './components/SelectionHeader';
import SourceLocationSection from './components/SourceLocationSection';
import DatDelimitersSection from './components/DatDelimitersSection';
import SourcesSection from './components/SourcesSection';
import DateTimeSection from './components/DateTimeSection';
import CfmHeader, {CfmHeaderTop} from './components/CfmHeader';
import CfmGrid from './components/CfmGrid';
import ArrModal from '../../common/arr-modal/ArrModal';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import ArrButton from '../../common/arr-button/ArrButton';
import {t} from 'i18next';
import {ToastType} from '../../../shared/constants';
import {RootState} from '../../../redux/store';
import {useIngestionForm} from './hooks/useIngestionForm';
import {useIngestionDataLoad} from './hooks/useIngestionDataLoad';
import {useIngestionMappingGrid} from './hooks/useIngestionMappingGrid';
import {
  handleSaveDraft,
  handleSaveDraftWithMappings,
} from './utils/saveDraftUtils';
import {
  usePostIngestionMapping,
  usePostIngestionSaveDraft,
} from '../../../hooks/ingestions/useIngestions';
import {getDuplicateDestinationNames} from './utils/ingestionMappingUtils';

const IngestionWizard = () => {
  const loginUserData = useSelector(
    (state: RootState) => state.authenticatedUser.user
  );

  const projectId = loginUserData.projectId;

  // Data loading (copy/edit flows)
  const {
    isCopyFlow,
    isEditFlow,
    isDataLoadFlow,
    isLoading: isDataLoading,
    loadedData,
    dataLoaded,
  } = useIngestionDataLoad();

  // Form state
  const {
    form,
    dropdownData,
    sourceHeadersData,
    folderAccessError,
    setFolderAccessError,
  } = useIngestionForm(
    loadedData,
    dataLoaded,
    isCopyFlow,
    isEditFlow,
    projectId
  );
  const values = form.getValues();
  // Grid state
  const {gridForm, mappingData, setMappingData} = useIngestionMappingGrid(
    loadedData,
    dataLoaded
  );
  const isIngestionTypeOverlay =
    values.ingestionType?.key === '2' ||
    values.ingestionType?.key === 'Overlay';
  const isIngestionTypeOverlayAndNoDat =
    isIngestionTypeOverlay && !values.sources.dat.enabled;
  // Track folder changes for all flows
  const [showFolderChangeModal, setShowFolderChangeModal] = useState(false);
  const [originalSourceFolder, setOriginalSourceFolder] = useState<
    string | null
  >(null);
  const [hasMappingData, setHasMappingData] = useState(false);
  // Track pending action after text confirmation
  const [_pendingMappingAction, setPendingMappingAction] = useState(false);
  // Track if we've already stored the original folder (prevents re-runs)
  const hasStoredOriginalFolder = useRef(false);

  // UI State
  const [leftLocked, setLeftLocked] = useState(false);
  const [rightPaneEnabled, setRightPaneEnabled] = useState(false);
  const [ingestionId, setIngestionId] = useState<number | null>(null);

  // Toast states
  const [saveDraftSuccess, setSaveDraftSuccess] = useState(false);
  const [saveDraftError, setSaveDraftError] = useState<string | null>(null);
  const [mappingDraftValidationError, setMappingDraftValidationError] =
    useState<string | null>(null);
  const [isSourceSystemError, setIsSourceSystemError] = useState(false);
  const [duplicateMappingTexts, setDuplicateMappingTexts] = useState('');
  // Modal states
  const [showTextConfirmModal, setShowTextConfirmModal] = useState(false);
  const [
    showUnmappedConfirmModalForDraft,
    setShowUnmappedConfirmModalForDraft,
  ] = useState(false);
  
  // Maintaining for when User selects `Is Path in DAT File?` but DAT is not selected
  const [sourceSelectionDATError, setSourceSelectionDATError] = useState(false);

  // Save draft mutation
  const saveDraftMutation = usePostIngestionSaveDraft();

  // Auto-enable right pane for copy/edit flows AND track original folder
  useEffect(() => {
    if (!loadedData || !dataLoaded) return;

    // Set ingestionId for edit flow
    if (
      isEditFlow &&
      loadedData.ingestionWizardModel.ingestionSpecifySetting.ingestionId
    ) {
      setIngestionId(
        Number(
          loadedData.ingestionWizardModel.ingestionSpecifySetting.ingestionId
        )
      );
    }

    // Store original source folder for Copy/Edit flows (only once using ref)
    if ((isCopyFlow || isEditFlow) && !hasStoredOriginalFolder.current) {
      const folderName =
        loadedData.ingestionWizardModel.ingestionSpecifySetting.folderName;
      setOriginalSourceFolder(folderName);
      hasStoredOriginalFolder.current = true;
    }

    // Enable right pane if mapping exists
    if (
      loadedData.ingestionWizardModel.ingestionMapping &&
      loadedData.ingestionWizardModel.ingestionMapping.length > 0
    ) {
      setLeftLocked(true);
      setRightPaneEnabled(true);
      setHasMappingData(true);
    }
  }, [loadedData, dataLoaded, isEditFlow, isCopyFlow]);

  // Mapping mutation
  const mappingMutation = usePostIngestionMapping(
    (data) => {
      setMappingData(data);
      setLeftLocked(true);
      setRightPaneEnabled(true);
    },
    (error) => {
      console.warn('', error);
      setSaveDraftError('Failed to load mapping data');
    }
  );

  // Track when mapping data is loaded (for all flows)
  useEffect(() => {
    if (mappingData) {
      if (mappingData.rowCount > 0) setHasMappingData(true);
    }
  }, [mappingData]);

  // Save draft handlers
  const saveDraftContext = {
    form,
    gridForm,
    sourceHeadersData,
    lstServerLocation: dropdownData.lstServerLocation,
    ingestionId,
    setIngestionId,
    setSaveDraftSuccess,
    setSaveDraftError,
    setMappingDraftValidationError,
    setShowUnmappedConfirmModalForDraft,
    mappingData,
    saveDraftMutation,
    isIngestionTypeOverlayAndNoDat,
  };

  const handleSaveDraftClick = () => {
    if (!leftLocked) {
      handleSaveDraft(saveDraftContext);
    } else {
      const duplicateTexts = getDuplicateDestinationNames(
        gridForm.getValues().columnMappings
      );
      if (duplicateTexts.length > 0) {
        setDuplicateMappingTexts(duplicateTexts.join(', '));
        return;
      } else {
        handleSaveDraftWithMappings(saveDraftContext, false);
      }
    }
  };

  // Handle "Yes" button on unmapped rows modal
  const handleUnmappedConfirmYes = () => {
    setShowUnmappedConfirmModalForDraft(false);
    handleSaveDraftWithMappings(saveDraftContext, true);
  };

  // Mapping handlers
  const handleNext = () => {
    form.handleSubmit(
      (values) => {
        const checkSourcesEnabled = (sources: SourcesState): boolean => {
          return Object.values(sources).some((s) => s.enabled);
        };
        const isSourceSelected = checkSourcesEnabled(values.sources);
        // Add Only always needs DAT (same as legacy)
        const isAddOnlyAndNoDat =
          (values.ingestionType?.key === '1' || values.ingestionType?.key === 'Add') && !values.sources.dat.enabled;
        if (!isSourceSelected || isAddOnlyAndNoDat) {
          setIsSourceSystemError(true);
          return;
        }
        const textSourceEnabled = values.sources.text.enabled;
        const isIngestionTypeOverlayAndNoDat =
          (values.ingestionType?.key === '2' || values.ingestionType?.key === 'Overlay') && !values.sources.dat.enabled;
        if (!textSourceEnabled && !isIngestionTypeOverlayAndNoDat) {
          setShowTextConfirmModal(true);
          return;
        }

        checkFolderChangeAndProceed();
        setTimeout(() => {
          (
            document.getElementById('cfm-section-title') as HTMLElement
          )?.focus();
        }, 0);
      },
      () => {}
    )();
  };

  const checkFolderChangeAndProceed = () => {
    const currentFolder = form.getValues('sourceFolder');

    if ((isCopyFlow || isEditFlow) && hasMappingData) {
      setShowFolderChangeModal(true);
      return;
    }

    if (!isCopyFlow && !isEditFlow && hasMappingData && originalSourceFolder) {
      const folderChanged =
        currentFolder?.displayValue !== originalSourceFolder;

      if (folderChanged) {
        setShowFolderChangeModal(true);
        return;
      }
    }

    if (!isCopyFlow && !isEditFlow && !originalSourceFolder && currentFolder) {
      setOriginalSourceFolder(currentFolder.displayValue);
    }

    proceedWithMapping();
  };

  const proceedWithMapping = () => {
    setPendingMappingAction(false);

    const values = form.getValues();
    const actionCode =
      values.ingestionType?.key === '1' || values.ingestionType?.key === 'Add'
        ? 'Add'
        : 'Overlay';
    const selectedKeyColumn = values.sources.dat.documentKey?.key ?? '';
    const columnHeaders =
      sourceHeadersData?.list?.map((item) => ({
        disabled: true,
        group: {},
        selected: false,
        text: item.value,
        value: String(item.key),
      })) ?? [];

    mappingMutation.mutate({
      columnHeaders,
      actionCode,
      selectedKeyColumn,
    });
  };

  // Handle "Yes" on text confirmation modal
  const handleTextConfirmYes = () => {
    setShowTextConfirmModal(false);
    setPendingMappingAction(true);

    checkFolderChangeAndProceed();
    setTimeout(() => {
      (document.getElementById('cfm-section-title') as HTMLElement)?.focus();
    }, 0);
  };

  const handleTextConfirmNo = () => {
    setShowTextConfirmModal(false);
    setPendingMappingAction(false);

    setTimeout(() => {
      (
        document.querySelector('[data-testid="next-button"]') as HTMLElement
      )?.focus();
    }, 0);
  };

  const handleFolderChangeConfirm = () => {
    setShowFolderChangeModal(false);

    setMappingData(null);
    gridForm.reset();
    setHasMappingData(false);

    const currentFolder = form.getValues('sourceFolder');
    const newFolder = currentFolder?.displayValue ?? null;

    setOriginalSourceFolder(newFolder);

    if (newFolder) {
      hasStoredOriginalFolder.current = true;
    }

    setLeftLocked(false);
    setRightPaneEnabled(false);

    proceedWithMapping();
  };

  const handleFolderChangeCancel = () => {
    setShowFolderChangeModal(false);
    setPendingMappingAction(false);
  };

  const handleBack = () => {
    setLeftLocked(false);
    setRightPaneEnabled(false);

    const currentFolder = form.getValues('sourceFolder');
    const newFolder = currentFolder?.displayValue ?? null;

    setOriginalSourceFolder(newFolder);

    if (newFolder) {
      hasStoredOriginalFolder.current = true;
    }

    setTimeout(() => {
      (
        document.getElementById('selection-section-title') as HTMLElement
      )?.focus();
    }, 0);
  };

  if (isDataLoadFlow && isDataLoading) {
    return (
      <Box
        className="ingestion-wizard"
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <Typography data-testid={`index-loading-ingestion-data`}>Loading ingestion data...</Typography>
      </Box>
    );
  }
  
  return (
    <Box className="ingestion-wizard">
      <Header
        onSaveDraft={handleSaveDraftClick}
        isSaving={saveDraftMutation.isPending}
      />
      <form
        onSubmit={form.handleSubmit(() => {})}
        noValidate
        data-testid={`index-form`}>
        <Grid container spacing={0} className="ingestion-grid">
          <Grid className="left-pane">
            <SelectionHeader
              isNextButtonDisabled={
                leftLocked ||
                form.formState.isSubmitting ||
                mappingMutation.isPending
              }
            />
            <SourceLocationSection
              control={form.control}
              errors={form.formState.errors}
              sourceSystem={dropdownData.sourceSystem}
              serverLocation={dropdownData.serverLocation}
              options={dropdownData.sourceFolderOptions}
              ingestionType={dropdownData.ingestionTypeOptions}
              disabled={leftLocked}
              clearErrors={form.clearErrors}
              setValue={form.setValue}
              isEditFlow={isEditFlow}
              isCopyFlow={isCopyFlow}
              isSubmitting={
                form.formState.isSubmitting || mappingMutation.isPending
              }
              onNext={handleNext}
            />
            <Divider
              orientation="horizontal"
              flexItem
              className="arr-divider"
            />

            <DatDelimitersSection
              control={form.control}
              errors={form.formState.errors}
              columnDelimeter={dropdownData.columnDelimeter}
              dataDelimeter={dropdownData.dataDelimeter}
              textQualifier={dropdownData.textQualifier}
              disabled={leftLocked}
            />
            <Divider
              orientation="horizontal"
              flexItem
              className="arr-divider"
            />

            <SourcesSection
              control={form.control}
              setValue={form.setValue}
              watch={form.watch}
              errors={form.formState.errors}
              loadFiles={dropdownData.loadFiles}
              headersData={dropdownData.headersDocData}
              disabled={leftLocked}
              linkType={dropdownData.linkType}
              setSourceSelectionDATError={setSourceSelectionDATError}
              annotationLayerOptions={dropdownData.annotationLayerOptions}
              hideRedactedNative
            />
            <Divider
              orientation="horizontal"
              flexItem
              className="arr-divider"
            />

            <DateTimeSection
              control={form.control}
              errors={form.formState.errors}
              timeZone={dropdownData.timeZone}
              dateFormat={dropdownData.dateFormat}
              disabled={leftLocked}
            />
          </Grid>

          <Grid className="right-pane">
            {/* 15-16: Title, Info Icon, Description */}
            <CfmHeaderTop rightPaneEnabled={rightPaneEnabled} />

            <Divider
              orientation="horizontal"
              flexItem
              className="arr-divider"
            />

            {/* 17-18: Mapping Grid + Add Button */}
            <CfmGrid
              mappingData={mappingData}
              disabled={!rightPaneEnabled}
              _gridControl={gridForm.control}
              gridSetValue={gridForm.setValue}
              _gridWatch={gridForm.watch}
              formValues={form.getValues()}
              isLeftLock={leftLocked}
            />

            {/* 19-20: Back + Preview buttons (visually positioned at top via CSS) */}
            <CfmHeader
              rightPaneEnabled={rightPaneEnabled}
              onBack={handleBack}
              formValues={form.getValues()}
              mappingData={mappingData}
              lstServerLocation={dropdownData.lstServerLocation}
              gridGetValues={gridForm.getValues}
              ingestionId={ingestionId}
              onSaveDraft={() =>
                handleSaveDraftWithMappings(saveDraftContext, true)
              }
            />
          </Grid>
        </Grid>
      </form>
      {/*
        21. Save as Draft Button — LAST in overall DOM/tab order.
        Placed outside the form and grid so it's after everything.
        Tab order: ... → Preview (20) → Save Draft (21).
        Visually positioned at the top-right of the page header
        via absolute positioning relative to .ingestion-wizard.
      */}
      <Box className="save-draft-button-a11y-container">
        <ArrButton
          aria-label={t('ingestions.Wizard.ariaLabels.header.saveDraftButton')}
          type="button"
          variant="primary"
          title="Save Draft"
          size="small"
          onClick={handleSaveDraftClick}
          onKeyDown={(e) => {
            if (e.key === 'Tab' && e.shiftKey) {
              e.preventDefault();
              (
                document.getElementById('page-description') as HTMLElement
              )?.focus();
              return;
            }

            if (e.key === 'Tab') {
              e.preventDefault();
              (
                document.getElementById(
                  'selection-section-title'
                ) as HTMLElement
              )?.focus();
            }
          }}
          disabled={saveDraftMutation.isPending}
          loading={saveDraftMutation.isPending}
          dataTestId="save-draft-button"
        >
          {t('ingestions.Wizard.btnText')}
        </ArrButton>
      </Box>
      {/* Modals */}
      {showTextConfirmModal && (
        <ArrModal
          showModal={showTextConfirmModal}
          onClose={handleTextConfirmNo}
          title={t('ingestions.Wizard.textConfirmModal.title')}
          description={
            <div tabIndex={0}>
              {t('ingestions.Wizard.textConfirmModal.description')}
            </div>
          }
          ariaDescription={t('ingestions.Wizard.textConfirmModal.description')}
          autoFocusCloseButton={true}
          dataTestId="text-confirm-modal"
          buttons={[
            {
              id: '1',
              label: t('ingestions.Wizard.textConfirmModal.noButton'),
              onClick: handleTextConfirmNo,
              variant: 'secondary',
              dataTestId: 'tcm-no-btn',
              ariaLabel: 'close modal, do not continue',
            },
            {
              id: '2',
              label: t('ingestions.Wizard.textConfirmModal.yesButton'),
              onClick: handleTextConfirmYes,
              variant: 'primary',
              dataTestId: 'tcm-yes-btn',
              ariaLabel: 'close modal, continue',
            },
          ]}
        />
      )}
      {showFolderChangeModal && (
        <ArrModal
          showModal={showFolderChangeModal}
          onClose={handleFolderChangeCancel}
          title={t('ingestions.Wizard.folderChangeModal.title')}
          description={t('ingestions.Wizard.folderChangeModal.description')}
          ariaDescription={t('ingestions.Wizard.folderChangeModal.description')}
          autoFocusCloseButton={true}
          dataTestId="folder-change-modal"
          buttons={[
            {
              id: '1',
              label: t('ingestions.Wizard.folderChangeModal.noButton'),
              onClick: handleFolderChangeCancel,
              variant: 'secondary',
              dataTestId: 'fcm-no-btn',
            },
            {
              id: '2',
              label: t('ingestions.Wizard.folderChangeModal.yesButton'),
              onClick: handleFolderChangeConfirm,
              variant: 'primary',
              dataTestId: 'fcm-yes-btn',
            },
          ]}
        />
      )}
      {showUnmappedConfirmModalForDraft && (
        <ArrModal
          showModal={showUnmappedConfirmModalForDraft}
          onClose={() => setShowUnmappedConfirmModalForDraft(false)}
          title="Confirm"
          description="You have several unmapped rows, which will be ignored and discarded."
          dataTestId="unmapped-confirm-modal"
          buttons={[
            {
              id: '1',
              label: 'No',
              onClick: () => setShowUnmappedConfirmModalForDraft(false),
              variant: 'secondary',
              dataTestId: 'ucm-no-btn',
            },
            {
              id: '2',
              label: 'Yes',
              onClick: handleUnmappedConfirmYes,
              variant: 'primary',
              dataTestId: 'ucm-yes-btn',
            },
          ]}
        />
      )}
      {/* Toasts */}
      {saveDraftSuccess && (
        <ArrToastNotification
          message={t('ingestions.Wizard.saveDraftSuccess')}
          onClose={() => setSaveDraftSuccess(false)}
          status={[ToastType.Success]}
        />
      )}
      {saveDraftError && (
        <ArrToastNotification
          message={saveDraftError}
          onClose={() => setSaveDraftError(null)}
          status={[ToastType.Error]}
        />
      )}
      {mappingDraftValidationError && (
        <ArrToastNotification
          message={mappingDraftValidationError}
          onClose={() => setMappingDraftValidationError(null)}
          status={[ToastType.Error]}
        />
      )}
      {folderAccessError && (
        <ArrToastNotification
          message={t('ingestions.Wizard.folderAccessError')}
          onClose={() => setFolderAccessError(false)}
          status={[ToastType.Error]}
        />
      )}
      {isSourceSystemError && (
        <ArrToastNotification
          message={
            isIngestionTypeOverlay
              ? t('ingestions.Wizard.sourceSystemOverlayErrorMsg')
              : t('ingestions.Wizard.sourceSystemAddOnlyErrorMsg')
          }
          onClose={() => setIsSourceSystemError(false)}
          status={[ToastType.Warning]}
        />
      )}
      {duplicateMappingTexts && (
        <ArrToastNotification
          message={t('ingestions.Wizard.cfm.duplicateMappingError', {
            fieldName: duplicateMappingTexts,
          })}
          onClose={() => setDuplicateMappingTexts('')}
          status={[ToastType.Error]}
        />
      )}
      {sourceSelectionDATError && (
        <ArrToastNotification
          message={t('ingestions.Wizard.sourcesSection.sourceSelectionDATError')}
          onClose={() => setSourceSelectionDATError(false)}
          status={[ToastType.Warning]}
        />
      )}
    </Box>
  );
};

export default IngestionWizard;
